import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowUpRight, Check, Loader2, MessageCircle, Heart,
  Sparkles, Crown, Moon, Diamond, Flower2, Phone, Calendar, Bed,
  Utensils, Cake, Camera, Video, Car, Gift, User, Flame, Church,
  MapPin, Users, Info,
} from "lucide-react";
import {
  ANNIVERSARY_TYPES, ANNIVERSARY_PACKAGES, ANNIVERSARY_INCLUDES,
  ANNIVERSARY_ADDONS, ANNIVERSARY_HERO_IMAGE, getAnniversary, getPackage, inr,
} from "@/lib/anniversaryPackages";
import { BRAND, whatsappLink } from "@/lib/brand";
import { submitEnquiry } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const ANN_ICONS = { flower: Flower2, sparkles: Sparkles, moon: Moon, crown: Crown, diamond: Diamond };
const INC_ICONS = { temple: Church, flame: Flame, flower: Flower2, bed: Bed, utensils: Utensils, cake: Cake, camera: Camera, video: Video, heart: Heart, car: Car, gift: Gift, user: User };

const PORTAL_TABS = [
  { k: "stay", label: "Stays", to: "/book" },
  { k: "yatra", label: "Yatra", to: "/book/yatra" },
  { k: "wedding", label: "Weddings", to: "/book/wedding" },
  { k: "anniversary", label: "Anniversary", to: "/book/anniversary" },
  { k: "event", label: "Events", to: "/book/event" },
  { k: "transport", label: "Transport", to: "/book/transport" },
];

// ============================================================================
export default function BookAnniversary() {
  const [params, setParams] = useSearchParams();
  const annId = params.get("a");
  const anniversary = useMemo(() => (annId ? getAnniversary(annId) : null), [annId]);
  const nav = useNavigate();

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [annId]);

  return (
    <div className="bg-kc min-h-screen" data-testid="portal-anniversary">
      <HeroSection anniversary={anniversary} onClear={() => setParams({})} nav={nav} />
      {!anniversary
        ? <TypeGrid onOpen={(id) => setParams({ a: id })} />
        : <AnniversaryDetail anniversary={anniversary} onBack={() => setParams({})} />
      }
    </div>
  );
}

// ============================================================================
const HeroSection = ({ anniversary, onClear, nav }) => (
  <section className="relative pt-32 md:pt-40 pb-14 md:pb-20 bg-black-lux text-[#F8F6F2] overflow-hidden" data-testid="anniversary-hero">
    <img src={anniversary?.image || ANNIVERSARY_HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,12,0.7), rgba(11,11,12,0.95))" }} />
    <div className="relative max-w-[1500px] mx-auto px-6 md:px-12">
      <PortalSwitch current="anniversary" />

      <nav className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-[#F8F6F2]/60 flex-wrap">
        <button onClick={() => nav("/")} className="hover:text-gold transition-colors" data-testid="ann-crumb-home">Home</button>
        <span className="opacity-40">/</span>
        <button onClick={() => nav("/book/wedding")} className="hover:text-gold transition-colors">Wedding</button>
        <span className="opacity-40">/</span>
        <button onClick={onClear} className={!anniversary ? "text-gold" : "hover:text-gold transition-colors"}>Anniversary</button>
        {anniversary && (<>
          <span className="opacity-40">/</span>
          <span className="text-gold">{anniversary.name}</span>
        </>)}
      </nav>

      <div className="mt-10 max-w-4xl">
        {!anniversary ? (<>
          <div className="overline text-gold flex items-center gap-2"><Heart size={11} className="fill-gold" /> Wedding Anniversary Celebrations</div>
          <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
            Return where <br /><span className="italic text-gold">it all began.</span>
          </h1>
          <p className="mt-6 text-[15px] md:text-[16px] leading-[1.8] text-[#F8F6F2]/75 max-w-2xl">
            The temple where Shiva and Parvati took their pheras remembers every couple who returns. From the first year to the diamond decade — a sacred anniversary celebration held with the reverence of a first wedding.
          </p>
        </>) : (<>
          <div className="text-3xl md:text-5xl">{anniversary.symbol}</div>
          <div className="overline text-gold mt-4">{anniversary.subtitle}</div>
          <h1 className="font-serif-display text-4xl md:text-6xl mt-4 font-light leading-[1.02] tracking-[-0.02em]">{anniversary.name}</h1>
          <p className="mt-6 text-[15px] md:text-[16px] leading-[1.8] text-[#F8F6F2]/75 max-w-2xl">{anniversary.story}</p>
          <div className="mt-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-gold">
            <Sparkles size={11} /> {anniversary.tagline}
          </div>
        </>)}
      </div>
    </div>
  </section>
);

const PortalSwitch = ({ current }) => (
  <div className="flex flex-wrap gap-1 border border-[#D4AF37]/25 p-1 self-start w-fit" data-testid="portal-switch">
    {PORTAL_TABS.map(t => (
      <Link key={t.k} to={t.to} className={`px-4 py-2 text-[10px] uppercase tracking-[0.24em] transition-colors ${current === t.k ? "bg-gold text-[#0F0F0F]" : "text-[#F8F6F2]/70 hover:text-gold"}`} data-testid={`portal-tab-${t.k}`}>
        {t.label}
      </Link>
    ))}
  </div>
);

// ============================================================================
// STEP 1 · TYPE GRID
// ============================================================================
const TypeGrid = ({ onOpen }) => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="anniversary-grid">
    <Reveal className="text-center mb-14 md:mb-20 max-w-3xl mx-auto">
      <div className="overline">Choose Your Milestone</div>
      <h2 className="font-serif-display text-3xl md:text-5xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">
        Five milestones · <span className="italic text-gold">one sacred shrine.</span>
      </h2>
      <p className="mt-6 text-[14px] text-ink-soft leading-[1.75]">Every celebration begins with a Temple Blessing at Triyuginarayan and unfolds across your chosen package — Premium, Silver, or Diamond.</p>
    </Reveal>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {ANNIVERSARY_TYPES.map((a, i) => <TypeCard key={a.id} anniversary={a} index={i} onOpen={onOpen} />)}
    </div>
  </section>
);

const TypeCard = ({ anniversary, index, onOpen }) => {
  const Icon = ANN_ICONS[anniversary.icon] || Heart;
  return (
    <Reveal delay={index * 80}>
      <div className="group bg-surface border border-kc h-full flex flex-col overflow-hidden hover:border-gold/50 transition-all" data-testid={`ann-card-${anniversary.id}`}>
        <div className="relative h-64 overflow-hidden">
          <img src={anniversary.image} alt={anniversary.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(11,11,12,0.75) 100%)" }} />
          <div className="absolute top-5 left-5 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-gold bg-black-lux/80 px-3 py-1.5 border border-gold/30">
            <Icon size={11} strokeWidth={1.5} /> № {String(index + 1).padStart(2, "0")}
          </div>
          <div className="absolute bottom-4 left-5 right-5 text-[#F8F6F2]">
            <div className="text-3xl mb-2">{anniversary.symbol}</div>
            <div className="font-serif-display text-2xl md:text-[26px] leading-tight">{anniversary.name}</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-gold mt-2">{anniversary.subtitle}</div>
          </div>
        </div>

        <div className="p-6 md:p-7 flex flex-col flex-1">
          <p className="text-[13.5px] leading-[1.75] text-ink-soft italic">"{anniversary.tagline}"</p>

          <div className="mt-5 pt-5 border-t border-kc">
            <div className="overline no-line">Temple Blessing</div>
            <p className="mt-2 text-[12.5px] text-ink-mute leading-[1.65]">{anniversary.blessing}</p>
          </div>

          <div className="mt-6 pt-5 border-t border-kc flex gap-2">
            <button onClick={() => onOpen(anniversary.id)} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-[10px] uppercase tracking-[0.24em] border border-gold/40 text-gold hover:bg-gold hover:text-[#0F0F0F] transition-colors" data-testid={`ann-view-${anniversary.id}`}>
              View Details <ArrowUpRight size={11} />
            </button>
            <button onClick={() => onOpen(anniversary.id)} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-[10px] uppercase tracking-[0.24em] bg-gold text-[#0F0F0F] hover:bg-[#F8F6F2] transition-colors" data-testid={`ann-book-${anniversary.id}`}>
              Book Now
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

// ============================================================================
// ANNIVERSARY DETAIL VIEW (packages · inclusions · add-ons · form)
// ============================================================================
const AnniversaryDetail = ({ anniversary, onBack }) => {
  const [selectedPkg, setSelectedPkg] = useState("silver");

  return (
    <>
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pt-14 pb-8">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-ink-soft hover:text-gold transition-colors" data-testid="ann-back">
          <ArrowLeft size={13} /> Back to All Anniversaries
        </button>
      </section>

      <TempleBlessingBlock anniversary={anniversary} />
      <PackageSelector anniversary={anniversary} selected={selectedPkg} onSelect={setSelectedPkg} />
      <InclusionsBlock />
      <AddonsBlock />
      <BookingBlock anniversary={anniversary} selectedPkg={selectedPkg} />
    </>
  );
};

// ----- Temple Blessing Highlights ---------
const TempleBlessingBlock = ({ anniversary }) => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 pb-14 md:pb-20" data-testid="ann-blessing">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <div className="overline">Temple Blessing Experience</div>
        <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
          Held with <br /><span className="italic text-gold">reverence.</span>
        </h2>
        <p className="mt-6 text-[14px] text-ink-soft leading-[1.75]">{anniversary.blessing}</p>

        <div className="mt-10 space-y-4">
          <MetaRow icon={Church}   label="Venue"      value="Triyuginarayan Temple · Kedarnath valley" />
          <MetaRow icon={MapPin}   label="Base"       value="Kedarnath Cottage · Sersi (35 km from Rudraprayag)" />
          <MetaRow icon={Calendar} label="Best Time"  value="Apr–Jun · Sep–Nov (year-round with cold-weather planning)" />
        </div>
      </div>

      <ul className="lg:col-span-7 space-y-4" data-testid="ann-highlights">
        {anniversary.highlights.map((h, i) => (
          <Reveal key={i} delay={i * 60} className="flex items-start gap-4 pb-4 border-b border-kc">
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold pt-1 flex-shrink-0">№ {String(i + 1).padStart(2, "0")}</div>
            <p className="text-[15px] leading-[1.75] text-ink">{h}</p>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const MetaRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold flex-shrink-0"><Icon size={16} strokeWidth={1.4} /></div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-gold">{label}</div>
      <div className="text-[14px] mt-1 text-ink">{value}</div>
    </div>
  </div>
);

// ----- Package Selector (3 tiers) ---------
const PackageSelector = ({ anniversary, selected, onSelect }) => (
  <section className="bg-cream section-pad" data-testid="ann-packages">
    <div className="max-w-[1500px] mx-auto px-6 md:px-12">
      <Reveal className="mb-12 text-center max-w-2xl mx-auto">
        <div className="overline">Choose Your Package</div>
        <h2 className="font-serif-display text-3xl md:text-5xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">
          Three tiers · <span className="italic text-gold">one blessing.</span>
        </h2>
        <p className="mt-6 text-[14px] text-ink-soft leading-[1.75]">All twelve inclusions in every package. Select the tier that matches your guest count and celebration scale.</p>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {ANNIVERSARY_PACKAGES.map((p, i) => (
          <Reveal key={p.id} delay={i * 80}>
            <button onClick={() => onSelect(p.id)} className={`w-full text-left p-7 md:p-9 border h-full flex flex-col transition-all ${selected === p.id ? "bg-black-lux text-[#F8F6F2] border-gold" : p.featured ? "bg-surface border-gold/40 hover:border-gold" : "bg-surface border-kc hover:border-gold/50"}`} data-testid={`ann-pkg-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-3xl">{p.symbol}</div>
                {p.featured && <span className="text-[9px] uppercase tracking-[0.28em] bg-gold text-[#0F0F0F] px-2 py-0.5">Signature</span>}
                {selected === p.id && <span className="text-[9px] uppercase tracking-[0.28em] bg-gold text-[#0F0F0F] px-2 py-0.5">✓ Selected</span>}
              </div>
              <div className={`overline no-line mt-6 ${selected === p.id ? "text-gold" : ""}`}>{p.tagline}</div>
              <div className="font-serif-display text-2xl md:text-[28px] mt-3 leading-tight font-light">{p.name}</div>
              <div className={`mt-2 text-[12px] uppercase tracking-[0.24em] ${selected === p.id ? "text-[#F8F6F2]/60" : "text-ink-mute"}`}>{p.guestLabel} · {p.duration}</div>

              <ul className={`mt-6 space-y-2.5 flex-1 ${selected === p.id ? "text-[#F8F6F2]/85" : "text-ink-soft"}`}>
                {p.perks.map((perk, k) => (
                  <li key={k} className="flex items-start gap-2 text-[12.5px] leading-relaxed">
                    <span className="mt-1.5 w-1 h-1 bg-gold flex-shrink-0" /> {perk}
                  </li>
                ))}
              </ul>

              <div className={`mt-7 pt-6 border-t flex items-baseline justify-between gap-3 ${selected === p.id ? "border-[#D4AF37]/30" : "border-kc"}`}>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Starting From</div>
                  <div className="font-serif-display text-2xl mt-1">{inr(p.startingFrom)}</div>
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] ${selected === p.id ? "text-gold" : "text-gold"}`}>
                  {selected === p.id ? "Selected" : "Select"} <ArrowUpRight size={11} />
                </span>
              </div>
            </button>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ----- Every Package Includes -----------
const InclusionsBlock = () => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-14 md:py-20" data-testid="ann-inclusions">
    <Reveal className="mb-12 max-w-3xl">
      <div className="overline">Every Package Includes</div>
      <h2 className="font-serif-display text-3xl md:text-5xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
        Twelve essentials · <span className="italic text-gold">held with care.</span>
      </h2>
      <p className="mt-4 text-[14px] text-ink-soft leading-[1.75]">Nothing is stripped out — every anniversary celebration ships with the full sacred + celebratory experience.</p>
    </Reveal>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
      {ANNIVERSARY_INCLUDES.map((inc, i) => {
        const Icon = INC_ICONS[inc.icon] || Check;
        return (
          <Reveal key={inc.key} delay={i * 40}>
            <div className="flex items-start gap-4 bg-surface border border-kc p-5 md:p-6 h-full" data-testid={`ann-inc-${inc.key}`}>
              <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold flex-shrink-0">
                <Icon size={16} strokeWidth={1.4} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-gold">№ {String(i + 1).padStart(2, "0")}</div>
                <div className="text-[14px] mt-1 text-ink leading-[1.5]">{inc.label}</div>
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

// ----- Optional Add-ons -----------------
const AddonsBlock = () => (
  <section className="bg-black-lux text-[#F8F6F2] section-pad" data-testid="ann-addons">
    <div className="max-w-[1500px] mx-auto px-6 md:px-12">
      <Reveal className="mb-12 max-w-3xl">
        <div className="overline no-line text-gold">Optional Add-Ons</div>
        <h2 className="font-serif-display text-3xl md:text-5xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
          Make it entirely <span className="italic text-gold">yours.</span>
        </h2>
        <p className="mt-4 text-[14px] text-[#F8F6F2]/65 leading-[1.75]">Available with any package. Prices are indicative — final quotation confirmed on WhatsApp.</p>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {ANNIVERSARY_ADDONS.map((a, i) => (
          <Reveal key={a.key} delay={i * 40}>
            <div className="bg-[#0F0F0F] border border-[#D4AF37]/25 p-5 md:p-6 h-full flex items-start justify-between gap-4" data-testid={`ann-addon-${a.key}`}>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.24em] text-gold">№ {String(i + 1).padStart(2, "0")}</div>
                <div className="text-[14px] mt-2 leading-[1.5]">{a.label}</div>
              </div>
              <div className="text-gold whitespace-nowrap text-[12px] font-serif-display">{inr(a.price)}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================================
// BOOKING BLOCK · form + 5 CTAs
// ============================================================================
const BookingBlock = ({ anniversary, selectedPkg }) => {
  const pkg = getPackage(selectedPkg);
  const [form, setForm] = useState({
    celebration_type: anniversary.name,
    celebration_date: "",
    package: pkg.name,
    guests: "",
    accommodation: "yes",
    transport: "yes",
    name: "",
    mobile: "",
    email: "",
    message: "",
    addons: [],
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleAddon = (k) => setForm(f => ({ ...f, addons: f.addons.includes(k) ? f.addons.filter(x => x !== k) : [...f.addons, k] }));

  // Keep package in sync when parent selection changes
  useEffect(() => { setForm(f => ({ ...f, package: pkg.name })); }, [pkg.name]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Please share your name and mobile number."); return; }
    setBusy(true);
    try {
      const addonLabels = form.addons.map(k => ANNIVERSARY_ADDONS.find(x => x.key === k)?.label).filter(Boolean).join(" · ");
      const payload = {
        type: "anniversary",
        name: form.name, mobile: form.mobile, email: form.email || undefined,
        destination: form.celebration_type,      // anniversary type
        hotel:       form.package,                // package tier
        check_in:    form.celebration_date || undefined,
        guests:      form.guests || undefined,
        room_type:   form.accommodation,          // yes/no marker
        city:        form.transport,              // reused: transport yes/no
        message:     [form.message, addonLabels && `Add-ons: ${addonLabels}`].filter(Boolean).join(" | ") || undefined,
      };
      const saved = await submitEnquiry(payload);
      const wa = buildWhatsApp(form, addonLabels);
      window.open(wa, "_blank", "noreferrer");
      setDone({ id: saved.id, url: wa });
      toast.success("Received. Namaste 🙏");
    } catch { toast.error("Something interrupted our line. Please WhatsApp us instead."); }
    finally { setBusy(false); }
  };

  if (done) return <SuccessScreen done={done} anniversary={anniversary} pkg={pkg} form={form} />;

  return (
    <section id="book" className="bg-black-lux text-[#F8F6F2] section-pad" data-testid="ann-booking">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

          {/* ----- Left · 5 CTA buttons + summary ----- */}
          <aside className="lg:col-span-4">
            <div className="overline no-line text-gold">Reserve · Enquire · Consult</div>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-tight">
              Every enquiry <br /><span className="italic text-gold">reaches us instantly.</span>
            </h2>

            <div className="mt-8 p-5 border border-[#D4AF37]/25 bg-[#0F0F0F]" data-testid="ann-summary">
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Your Selection</div>
              <div className="mt-3 font-serif-display text-[19px] leading-tight">{anniversary.name}</div>
              <div className="mt-2 text-[12px] text-[#F8F6F2]/60">{pkg.name} · {pkg.guestLabel} · {pkg.duration}</div>
              <div className="mt-4 pt-4 border-t border-[#D4AF37]/25 flex items-baseline justify-between">
                <span className="text-[10px] uppercase tracking-[0.28em] text-gold">Starting From</span>
                <span className="font-serif-display text-xl">{inr(pkg.startingFrom)}</span>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-[#D4AF37]/25 space-y-3" data-testid="ann-ctas">
              <a href={buildWhatsApp(form, "")} target="_blank" rel="noreferrer" className="btn-gold w-full justify-center" data-testid="ann-cta-whatsapp">
                <MessageCircle size={14} /> WhatsApp Planner
              </a>
              <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="btn-ghost-light w-full justify-center" data-testid="ann-cta-call">
                <Phone size={13} /> Call Celebration Expert
              </a>
              <a href={whatsappLink(`Namaste 🙏 I'd like to schedule a video consultation for our ${anniversary.name}. When can we talk?`)} target="_blank" rel="noreferrer" className="btn-ghost-light w-full justify-center" data-testid="ann-cta-consult">
                <Calendar size={13} /> Schedule Consultation
              </a>
              <a href={whatsappLink(`Namaste 🙏 We would like a fully customized quote for our ${anniversary.name} — please share options.`)} target="_blank" rel="noreferrer" className="btn-ghost-light w-full justify-center" data-testid="ann-cta-quote">
                <Sparkles size={13} /> Get Custom Quote
              </a>
            </div>
          </aside>

          {/* ----- Right · Booking Form ----- */}
          <form onSubmit={submit} className="lg:col-span-8 bg-[#0F0F0F] border border-[#D4AF37]/25 p-8 md:p-12" data-testid="ann-form">
            <div className="overline no-line text-gold flex items-center gap-2"><Heart size={11} /> Book Celebration</div>
            <h3 className="font-serif-display text-3xl mt-4 leading-tight">Begin your celebration story</h3>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Select label="Celebration Type" v={form.celebration_type} u={v => upd("celebration_type", v)} testid="ann-f-type" options={ANNIVERSARY_TYPES.map(a => a.name)} />
              <Field label="Celebration Date" type="date" v={form.celebration_date} u={v => upd("celebration_date", v)} testid="ann-f-date" />
              <Select label="Package Selection" v={form.package} u={v => upd("package", v)} testid="ann-f-pkg" options={ANNIVERSARY_PACKAGES.map(p => p.name)} />
              <Field label="Number of Guests" v={form.guests} u={v => upd("guests", v)} testid="ann-f-guests" placeholder={`e.g. ${pkg.guestMax}`} />
              <Select label="Accommodation Required" v={form.accommodation} u={v => upd("accommodation", v)} testid="ann-f-acc" options={["yes","no"]} />
              <Select label="Transportation Required" v={form.transport} u={v => upd("transport", v)} testid="ann-f-trs" options={["yes","no"]} />

              <div className="md:col-span-2 pt-2">
                <div className="overline no-line text-gold mb-3">Optional Add-Ons</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {ANNIVERSARY_ADDONS.map(a => (
                    <button type="button" key={a.key} onClick={() => toggleAddon(a.key)} className={`text-left text-[12px] px-4 py-3 border transition-colors flex items-center justify-between gap-3 ${form.addons.includes(a.key) ? "bg-gold text-[#0F0F0F] border-gold" : "border-[#D4AF37]/25 text-[#F8F6F2]/85 hover:border-gold"}`} data-testid={`ann-f-addon-${a.key}`}>
                      <span className="flex items-center gap-2">
                        {form.addons.includes(a.key) ? <Check size={11} /> : <span className="w-3 h-3 border border-current" />} {a.label}
                      </span>
                      <span className={form.addons.includes(a.key) ? "" : "text-gold"}>{inr(a.price)}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Field label="Guest Name"      v={form.name}   u={v => upd("name", v)}   required testid="ann-f-name" />
              <Field label="Mobile Number"   v={form.mobile} u={v => upd("mobile", v)} required testid="ann-f-mobile" placeholder="+91 …" />
              <Field label="Email Address · Optional" v={form.email} u={v => upd("email", v)} col={2} testid="ann-f-email" placeholder="you@example.com" />
              <Field label="Special Requirements · Optional" v={form.message} u={v => upd("message", v)} col={2} textarea testid="ann-f-msg" placeholder="e.g. anniversary song, elderly access, dietary preferences" />
            </div>

            <button type="submit" disabled={busy} className="mt-10 btn-gold w-full md:w-auto justify-center" data-testid="ann-f-submit">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Heart size={13} className="fill-current" /> Book Celebration · Open WhatsApp <ArrowUpRight size={12} /></>}
            </button>
            <p className="mt-5 text-[10px] uppercase tracking-[0.24em] text-[#F8F6F2]/40">Every enquiry reaches Admin Dashboard, WhatsApp Business, and (once wired) Business Email instantly.</p>
          </form>
        </div>
      </div>
    </section>
  );
};

const buildWhatsApp = (form, addonLabels) => {
  const rows = [
    `Namaste 🙏 I would like to plan our ${form.celebration_type} at Triyuginarayan with THE KC INDIA.`, ``,
    `Package: ${form.package}`,
    form.celebration_date && `Celebration Date: ${form.celebration_date}`,
    form.guests && `Guests: ${form.guests}`,
    `Accommodation: ${form.accommodation}`,
    `Transportation: ${form.transport}`,
    addonLabels && `Add-Ons: ${addonLabels}`,
    form.message && `Special Requirements: ${form.message}`,
    ``,
    form.name && `Name: ${form.name}`,
    form.mobile && `Mobile: ${form.mobile}`,
  ].filter(Boolean);
  return whatsappLink(rows.join("\n"));
};

// ----- Success Screen ----------
const SuccessScreen = ({ done, anniversary, pkg, form }) => (
  <section className="bg-black-lux text-[#F8F6F2] section-pad" data-testid="ann-success">
    <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-gold/10 border border-gold flex items-center justify-center text-gold"><Check size={22} strokeWidth={2} /></div>
      <div className="overline no-line mt-8 text-gold">Namaste</div>
      <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight">
        Your <span className="italic text-gold">{anniversary.name}</span> enquiry has been received.
      </h2>
      <p className="mt-6 text-[#F8F6F2]/70 text-[15px] leading-relaxed max-w-lg mx-auto">
        Our celebration expert will personally reach you on <b>{form.mobile}</b> within 10 minutes with a detailed quotation for the <b>{pkg.name}</b>.
      </p>
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <a href={done.url} target="_blank" rel="noreferrer" className="btn-gold" data-testid="ann-open-wa"><MessageCircle size={14} /> Continue on WhatsApp</a>
        <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="btn-ghost-light" data-testid="ann-call-now"><Phone size={13} /> Call Now</a>
        <Link to="/book/anniversary" className="btn-ghost-light">Explore Other Anniversaries</Link>
      </div>
      <div className="mt-12 pt-8 border-t border-[#D4AF37]/25 text-[11px] uppercase tracking-[0.28em] text-[#F8F6F2]/40">Enquiry ID · {done.id.slice(0, 8)}</div>
    </div>
  </section>
);

// ----- Field primitives ----------
const Field = ({ label, v, u, required, col = 1, testid, textarea, type = "text", placeholder }) => (
  <div className={col === 2 ? "md:col-span-2" : ""}>
    <span className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2 block">{label}</span>
    {textarea
      ? <textarea rows={3} className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 text-[13.5px] text-[#F8F6F2] placeholder:text-[#F8F6F2]/40 focus:outline-none focus:border-gold transition-colors" placeholder={placeholder} value={v} onChange={e => u(e.target.value)} data-testid={testid} />
      : <input type={type} className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 text-[13.5px] text-[#F8F6F2] placeholder:text-[#F8F6F2]/40 focus:outline-none focus:border-gold transition-colors" placeholder={placeholder} value={v} onChange={e => u(e.target.value)} required={required} data-testid={testid} />
    }
  </div>
);

const Select = ({ label, v, u, options, testid }) => (
  <div>
    <span className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2 block">{label}</span>
    <select className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 text-[13.5px] text-[#F8F6F2] focus:outline-none focus:border-gold transition-colors [&>option]:bg-[#0F0F0F]" value={v} onChange={e => u(e.target.value)} data-testid={testid}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
